import { AfterViewInit, Component, ViewEncapsulation } from '@angular/core';
import { CommonService } from '@core/services/common.service';
import { ModalService } from '@core/services/modal.service';
import { ProfileService } from '@core/services/profile.service';
import { Address } from '@shared/models/address';
import { Profile } from '@shared/models/profile';
import { ProfileEk } from '@shared/models/profile-ek';
import { FormControl, FormGroup } from '@angular/forms';
import { AgentSource } from '@shared/models/codebooks/agent-source';
import moment, { Moment } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@core/services/alert.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ProfileComponent {
    public isShowedCheckboxes = false;

    public profile: Profile | null = null;
    public profileEk: ProfileEk | null = null;
    public formNotifications: FormGroup;

    public isDisabledChanges: boolean = false;

    public isReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public ecAgreementChecked: boolean = false;
    public mktChecked: boolean = false;

    constructor(
        private profileService: ProfileService,
        private commonService: CommonService,
        private modalService: ModalService,
        private translateService: TranslateService,
        private alertService: AlertService
    ) {
        this.profileService.getAccount().subscribe((profile) => {
            this.profile = profile;

            this.isDisabledChanges = profile.changeRequestOpened;

            this.ecAgreementChecked = profile.mailService.typeId === 1;

            this.translateService.get([
                'profile.texts.alert.request-opened',
                'profile.texts.alert.overdue-id'
            ]).subscribe((trans) => {

                let alertMsgs = [];
                if (profile.changeRequestOpened) {
                    alertMsgs.push(trans['profile.texts.alert.request-opened']);
                }

                if (profile.identityCard.validTo && moment(profile.identityCard.validTo).isBefore(moment.now())) {
                    alertMsgs.push(trans['profile.texts.alert.overdue-id']);
                }

                this.alertService.warn(alertMsgs.join('<br>'));
            });

            if (this.profile && this.profile.shouldUnifyContacts) {
                this.openModal('UnifyContacts');
            }

            this.profileService.getAccountEk(
                profile.personalNumber,
                profile.lastName
            ).subscribe((profileEk) => {
                this.profileEk = profileEk;
                this.mktChecked = profileEk.hasMarketingApproval;

                this.formNotifications = new FormGroup({
                    enabledNotifications: new FormControl(profileEk.hasNotificationApproval)
                });

                this.formNotifications.valueChanges.subscribe((changes) => {
                    this.profileService.postNotificationApproval(changes.enabledNotifications).subscribe({
                        error: () => {
                            this.formNotifications.get('enabledNotifications').setValue(
                                !changes.enabledNotifications,
                                {
                                    emitEvent: false
                                }
                            );
                        }
                    });
                });

                this.isReady.next(true);
            });
        });
    }

    public getPreferredAgentName(): string {
        return this.commonService.getLoggedUser()?.prefferedAgent?.agentName ?
            this.commonService.getLoggedUser().prefferedAgent.agentName : '';
    }

    public formatAddress(address: Address): string {

        let street = address.street;
        if (address.houseNumber && address.houseOrientationNumber) {
            street += ' ' + [
                address.houseNumber,
                address.houseOrientationNumber,
            ].join('/');
        }

        if (!address.houseNumber && address.houseOrientationNumber) {
            street += ' ' + address.houseOrientationNumber;
        }

        if (address.houseNumber && !address.houseOrientationNumber) {
            street += ' ' + address.houseNumber;
        }

        return [
            street,
            address.zip + ' ' + address.city,
            address.country.skCountryName
        ].join(', ');
    }

    public showConsents() {
        this.isShowedCheckboxes = true;
    }

    public changeEcAgreement(checked: boolean) {
        if (checked) {
            this.profileService.postAccountUpdate('ecAgreement', {
                email: this.profile.email,
                phoneNumber: this.profile.mobile,
                personalNumber: this.profile.personalNumber,
                mailService: this.profile.mailService,
            }).subscribe(() => {
                window.location.reload();
            });
        } else {
            this.modalService.openModal('CancelEcAgreement').toggle.subscribe(opened => {
                if (!opened) {
                    this.ecAgreementChecked = true;
                }
            });
        }
    }

    public changeMktApproval(checked: boolean) {
        if (checked) {
            this.profileService.postMKTApproval(
                this.profile.firstName,
                this.profile.lastName,
                this.profile.personalNumber,
                this.profile.partId,
            ).subscribe();
        } else {
            this.modalService.openModal('CancelMKT', {
                profile: this.profile
            }).toggle.subscribe(opened => {
                if (!opened) {
                    this.mktChecked = true;
                }
            });
        }
    }

    public openModal(type: string) {
        switch (type) {
            case 'PreferredAgent':
                this.commonService.openPrefferedAgentModal(null);
                break;
            default:
                this.modalService.openModal(type, {
                    profile: this.profile
                });
        }
    }

    public getAccessText(): string {
        switch (this.commonService.getLoggedUser().agentSource) {
            case AgentSource.LEVEL1:
                return 'agentSource.level1';
            case AgentSource.LEVEL2:
                return 'agentSource.level2';
            case AgentSource.LEVEL3:
                return 'agentSource.level3';
        }
    }
}
